import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { isEmpty } from 'lodash';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { COUNTRY_LIST, parseHttpError, t, embedApi, UserUtils } from '@cyberint/common-utils';
import {
  Button,
  CustomTooltip,
  Flag,
  FormControl,
  Icons,
  IconSizes,
  LoadingOverlay,
  Themes,
  TitleWithTooltip as Label
} from '@cyberint/dsc';
import * as S from './styled';

const { InputControl, AutocompleteControl } = FormControl;
const EMAIL = 'email';
const FIRST = 'firstname';
const LAST = 'lastname';
const PHONE = 'phone';
const COUNTRY = 'country';
const TENANT_ID = 'cp_tenant_id';

const COUNTRIES = COUNTRY_LIST.map(country => ({
  label: country.country,
  value: country.country,
  icon: country.code ? <Flag alt={country.country} code={country.code} size={22} /> : null,
  mappedValue: country.country
}));

const SCHEMA = yup.object().shape({
  [EMAIL]: yup.string().required(t('validation.required')).email(t('validation.email')),
  [FIRST]: yup.string().nullable().required(t('validation.required')),
  [LAST]: yup.string().nullable().required(t('validation.required')),
  [PHONE]: yup.string().nullable().required(t('validation.required')),
  [COUNTRY]: yup.string().nullable().required(t('validation.required'))
});

const PORTAL_ID = '2034462';
const FORM_ID = '87096693-2f8f-4932-b431-48aad7692498';
const HUBSPOT_FORM_URL = `https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${FORM_ID}`;
const PRIVACY_URL = 'https://cyberint.com/privacy-policy/';

const UPDATED_AT = 'updatedAt';

const buildValues = ({ user, tenantId }) => ({
  [EMAIL]: user?.email,
  [FIRST]: user?.firstName,
  [LAST]: user?.lastName,
  [PHONE]: user?.phone,
  [TENANT_ID]: tenantId
});

export const DemoPageForm = ({ user, tenantId }) => {
  const formMethods = useForm({
    mode: 'all',
    reValidateMode: 'onChange',
    resolver: yupResolver(SCHEMA),
    defaultValues: buildValues({ user, tenantId })
  });
  const {
    reset,
    handleSubmit,
    formState: { errors, isDirty },
    getValues
  } = formMethods;

  const [fetching, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDemoRequested, setDemoRequested] = useState('');
  const [error, setError] = useState(null);
  const [errorMessages, setErrorMessages] = useState([]);

  const isCheckpointUser = UserUtils.isCheckpointEmail(user?.email);

  useEffect(() => {
    if (isDirty || isDemoRequested) return;
    reset(buildValues({ user, tenantId }));
  }, [user, tenantId, isDirty]);

  useEffect(() => {
    setFetching(true);
    const getCachedDemo = async () => {
      try {
        const { data } = await embedApi.getCachedDemoPage({ tenantId });
        if (data[UPDATED_AT]) {
          setDemoRequested(t('demo_page.applied_before'));
        }
        setFetching(false);
      } catch {
        setFetching(false);
      }
    };

    getCachedDemo();
  }, [tenantId]);

  const onSubmit = async () => {
    const values = getValues();
    setError(null);
    setErrorMessages([]);
    try {
      setLoading(true);
      const fields = Object.entries(values).map(([name, value]) => ({ objectTypeId: '0-1', name, value }));
      await axios.post(HUBSPOT_FORM_URL, { fields });

      setDemoRequested(t('demo_page.applied_success'));
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setError(parseHttpError(e, t('demo_page.apply_fail')));
      setErrorMessages(e.response?.data?.errors?.map(err => err.message));
    }
    try {
      await embedApi.cacheDemoPage({ tenantId, userId: user?.email });
    } catch {
      /** Not critical */
    }
  };

  const errorsTooltip = errorMessages?.length > 0 && (
    <div>
      {errorMessages?.map(m => (
        <S.Error>{m}</S.Error>
      ))}
    </div>
  );

  if (isCheckpointUser) {
    return (
      <S.DemoFormWrapper $done>
        <S.SuccessMessage>
          <span>{t('demo_page.cp_employee_msg')}</span>
          <S.ClickHere
            href="https://wiki.checkpoint.com/confluence/display/SALESENAB/Infinity+External+Risk+Management"
            target="_blank"
            rel="noreferrer">
            {t('demo_page.cp_click_here')}
          </S.ClickHere>
        </S.SuccessMessage>
      </S.DemoFormWrapper>
    );
  }

  return (
    <S.DemoFormWrapper $done={!!isDemoRequested}>
      {fetching && <LoadingOverlay />}
      {isDemoRequested ? (
        <S.SuccessMessage>
          <Icons.CheckOutlined size={72} color="var(--white)" />
          {isDemoRequested}
        </S.SuccessMessage>
      ) : (
        <S.FormTitle>{t('demo_page.get_demo')}</S.FormTitle>
      )}
      {!isDemoRequested && (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <FormProvider {...formMethods}>
          <InputControl name={EMAIL} label={<Label label={t('demo_page.email')} required />} />
          <InputControl name={FIRST} label={<Label label={t('demo_page.firstName')} required />} />
          <InputControl name={LAST} label={<Label label={t('demo_page.lastName')} required />} />
          <InputControl name={PHONE} label={<Label label={t('demo_page.phone')} required />} />
          <AutocompleteControl
            name={COUNTRY}
            label={<Label label={t('demo_page.country')} required />}
            options={COUNTRIES}
            mode={Themes.LIGHT}
          />
          <S.Note>
            By submitting you give your consent with your personal data being processed as described above. You can read
            more about it on our{' '}
            <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
              privacy policy
            </a>
            .
          </S.Note>
          {!!error && (
            <S.Error>
              {error}
              {!!errorsTooltip && (
                <CustomTooltip mode={Themes.LIGHT} title={errorsTooltip} width={350}>
                  <div>
                    <Icons.Info size={IconSizes.S} color="var(--error)" />
                  </div>
                </CustomTooltip>
              )}
            </S.Error>
          )}
          <Button.Filled
            size="XLARGE"
            disabled={!isDirty || !isEmpty(errors)}
            onClick={handleSubmit(onSubmit)}
            loading={loading}>
            {t('demo_page.get_demo_button')}
          </Button.Filled>
        </FormProvider>
      )}
    </S.DemoFormWrapper>
  );
};

DemoPageForm.propTypes = {
  user: PropTypes.shape({}),
  tenantId: PropTypes.string
};
